// file: frontend/src/components/pages/admin/CreatePlanModal.jsx
import React, { useState } from "react";
import { X, DollarSign, MessageSquare, Users, FileText, Zap, Image } from "lucide-react";

const CreatePlanModal = ({ isOpen, onClose, onSubmit, isLoading, error: modalError }) => {
  const [formData, setFormData] = useState({
    name: "",
    planType: "individual",
    baseCost: "",
    dailyLimits: {
      highEndLlmTokens: "",
      smallLlmTokens: -1,
      images: "",
      webSearches: "",
      youtube: ""
    },
    monthlyLimits: {
      highEndLlmTokens: "",
      smallLlmTokens: -1,
      images: "",
      webSearches: "",
      youtube: ""
    }
  });
  const [error, setError] = useState("");

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNestedInputChange = (parentKey, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parentKey]: {
        ...prev[parentKey],
        [field]: value
      }
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(""); // Clear previous errors
    
    // Validate required fields
    if (!formData.name || !formData.baseCost || !formData.dailyLimits.highEndLlmTokens || !formData.dailyLimits.images || !formData.dailyLimits.webSearches || !formData.dailyLimits.youtube) {
      setError("Please fill all required fields");
      return;
    }

    const planData = {
      name: formData.name,
      planType: formData.planType,
      baseCost: parseFloat(formData.baseCost),
      dailyLimits: {
        highEndLlmTokens: parseInt(formData.dailyLimits.highEndLlmTokens),
        smallLlmTokens: formData.dailyLimits.smallLlmTokens,
        images: parseInt(formData.dailyLimits.images),
        webSearches: parseInt(formData.dailyLimits.webSearches),
        youtube: parseInt(formData.dailyLimits.youtube)
      },
      monthlyLimits: {
        highEndLlmTokens: parseInt(formData.monthlyLimits.highEndLlmTokens),
        smallLlmTokens: formData.monthlyLimits.smallLlmTokens,
        images: parseInt(formData.monthlyLimits.images),
        webSearches: parseInt(formData.monthlyLimits.webSearches),
        youtube: parseInt(formData.monthlyLimits.youtube)
      }
    };

    onSubmit(planData);
  };

  const handleClose = () => {
    setFormData({
      name: "",
      planType: "individual",
      baseCost: "",
      dailyLimits: {
        highEndLlmTokens: "",
        smallLlmTokens: -1,
        images: "",
        webSearches: "",
        youtube: ""
      },
      monthlyLimits: {
        highEndLlmTokens: "",
        smallLlmTokens: -1,
        images: "",
        webSearches: "",
        youtube: ""
      }
    });
    setError("");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-bg-60 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-bd-50">
          <h2 className="text-sm font-semibold text-fg-50">Create New Plan</h2>
          <button
            onClick={handleClose}
            className="text-fg-60 hover:text-fg-50 transition-colors"
            disabled={isLoading}
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 h-full flex flex-col">
          {/* Error Message */}
          {(error || modalError) && (
            <div className="text-red-500 text-sm bg-red-500/10 border border-red-500/20 rounded-md p-3 mb-6">
              {error || modalError}
            </div>
          )}

          {/* Basic Plan Info - 2 columns */}
          <div className="grid grid-cols-2 gap-6 mb-6">
            {/* Plan Name */}
            <div>
              <label className="block text-xs font-medium text-fg-50 mb-1.5">
                Plan Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="w-full px-3 py-2 bg-bg-50 border border-bd-50 rounded-md text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-02 text-xs"
                placeholder="e.g., basic, premium, custom"
                required
                disabled={isLoading}
              />
            </div>

            {/* Plan Type */}
            <div>
              <label className="block text-xs font-medium text-fg-50 mb-1.5">
                Plan Type *
              </label>
              <select
                value={formData.planType}
                onChange={(e) => handleInputChange('planType', e.target.value)}
                className="w-full px-3 py-2 bg-bg-50 border border-bd-50 rounded-md text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-02 text-xs"
                required
                disabled={isLoading}
              >
                <option value="individual">Individual</option>
                <option value="organization">Organization</option>
              </select>
            </div>
          </div>

          {/* Base Cost */}
          <div className="mb-6">
            <label className="block text-xs font-medium text-fg-50 mb-1.5">
              Base Cost *
            </label>
            <div className="relative">
              <DollarSign size={12} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-fg-60" />
              <input
                type="number"
                step="0.01"
                value={formData.baseCost}
                onChange={(e) => handleInputChange('baseCost', e.target.value)}
                className="w-full pl-8 pr-3 py-2 bg-bg-50 border border-bd-50 rounded-md text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-02 text-xs"
                placeholder="0.00"
                required
                disabled={isLoading}
              />
            </div>
          </div>

          {/* Daily Limits Section */}
          <div className="mb-6">
            <h4 className="text-sm font-medium text-fg-50 mb-3 border-b border-bd-50 pb-2">Daily Limits</h4>
            <div className="grid grid-cols-2 gap-4">
              {/* High-End LLM Tokens */}
              <div>
                <label className="block text-xs font-medium text-fg-50 mb-1.5">
                  High-End LLM Tokens *
                </label>
                <div className="relative">
                  <Zap size={12} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-fg-60" />
                  <input
                    type="number"
                    value={formData.dailyLimits.highEndLlmTokens}
                    onChange={(e) => handleNestedInputChange('dailyLimits', 'highEndLlmTokens', e.target.value)}
                    className="w-full pl-8 pr-3 py-2 bg-bg-50 border border-bd-50 rounded-md text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-02 text-xs"
                    placeholder="7000"
                    required
                    disabled={isLoading}
                  />
                </div>
              </div>

              {/* Images */}
              <div>
                <label className="block text-xs font-medium text-fg-50 mb-1.5">
                  Images *
                </label>
                <div className="relative">
                  <Image size={12} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-fg-60" />
                  <input
                    type="number"
                    value={formData.dailyLimits.images}
                    onChange={(e) => handleNestedInputChange('dailyLimits', 'images', e.target.value)}
                    className="w-full pl-8 pr-3 py-2 bg-bg-50 border border-bd-50 rounded-md text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-02 text-xs"
                    placeholder="3"
                    required
                    disabled={isLoading}
                  />
                </div>
              </div>

              {/* Web Searches */}
              <div>
                <label className="block text-xs font-medium text-fg-50 mb-1.5">
                  Web Searches *
                </label>
                <div className="relative">
                  <MessageSquare size={12} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-fg-60" />
                  <input
                    type="number"
                    value={formData.dailyLimits.webSearches}
                    onChange={(e) => handleNestedInputChange('dailyLimits', 'webSearches', e.target.value)}
                    className="w-full pl-8 pr-3 py-2 bg-bg-50 border border-bd-50 rounded-md text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-02 text-xs"
                    placeholder="10"
                    required
                    disabled={isLoading}
                  />
                </div>
              </div>

              {/* Small LLM Tokens */}
              <div>
                <label className="block text-xs font-medium text-fg-50 mb-1.5">
                  Small LLM Tokens
                </label>
                <div className="relative">
                  <MessageSquare size={12} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-fg-60" />
                  <input
                    type="number"
                    value={formData.dailyLimits.smallLlmTokens === -1 ? "" : formData.dailyLimits.smallLlmTokens}
                    onChange={(e) => {
                      const val = e.target.value === "" ? -1 : parseInt(e.target.value);
                      handleNestedInputChange('dailyLimits', 'smallLlmTokens', val);
                    }}
                    className="w-full pl-8 pr-3 py-2 bg-bg-50 border border-bd-50 rounded-md text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-02 text-xs"
                    placeholder="Leave empty for unlimited"
                    disabled={isLoading}
                  />
                  {formData.dailyLimits.smallLlmTokens === -1 && (
                    <span className="text-xs text-fg-60 mt-1 block">Unlimited</span>
                  )}
                </div>
              </div>

              {/* YouTube */}
              <div>
                <label className="block text-xs font-medium text-fg-50 mb-1.5">
                  YouTube Recaps *
                </label>
                <div className="relative">
                  <FileText size={12} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-fg-60" />
                  <input
                    type="number"
                    value={formData.dailyLimits.youtube}
                    onChange={(e) => handleNestedInputChange('dailyLimits', 'youtube', e.target.value)}
                    className="w-full pl-8 pr-3 py-2 bg-bg-50 border border-bd-50 rounded-md text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-02 text-xs"
                    placeholder="10"
                    required
                    disabled={isLoading}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Monthly Limits Section */}
          <div className="mb-6">
            <h4 className="text-sm font-medium text-fg-50 mb-3 border-b border-bd-50 pb-2">Monthly Limits</h4>
            <div className="grid grid-cols-2 gap-4">
              {/* High-End LLM Tokens */}
              <div>
                <label className="block text-xs font-medium text-fg-50 mb-1.5">
                  High-End LLM Tokens *
                </label>
                <div className="relative">
                  <Zap size={12} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-fg-60" />
                  <input
                    type="number"
                    value={formData.monthlyLimits.highEndLlmTokens}
                    onChange={(e) => handleNestedInputChange('monthlyLimits', 'highEndLlmTokens', e.target.value)}
                    className="w-full pl-8 pr-3 py-2 bg-bg-50 border border-bd-50 rounded-md text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-02 text-xs"
                    placeholder="210000"
                    required
                    disabled={isLoading}
                  />
                </div>
              </div>

              {/* Images */}
              <div>
                <label className="block text-xs font-medium text-fg-50 mb-1.5">
                  Images *
                </label>
                <div className="relative">
                  <Image size={12} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-fg-60" />
                  <input
                    type="number"
                    value={formData.monthlyLimits.images}
                    onChange={(e) => handleNestedInputChange('monthlyLimits', 'images', e.target.value)}
                    className="w-full pl-8 pr-3 py-2 bg-bg-50 border border-bd-50 rounded-md text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-02 text-xs"
                    placeholder="90"
                    required
                    disabled={isLoading}
                  />
                </div>
              </div>

              {/* Web Searches */}
              <div>
                <label className="block text-xs font-medium text-fg-50 mb-1.5">
                  Web Searches *
                </label>
                <div className="relative">
                  <MessageSquare size={12} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-fg-50" />
                  <input
                    type="number"
                    value={formData.monthlyLimits.webSearches}
                    onChange={(e) => handleNestedInputChange('monthlyLimits', 'webSearches', e.target.value)}
                    className="w-full pl-8 pr-3 py-2 bg-bg-50 border border-bd-50 rounded-md text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-02 text-xs"
                    placeholder="300"
                    required
                    disabled={isLoading}
                  />
                </div>
              </div>

              {/* Small LLM Tokens */}
              <div>
                <label className="block text-xs font-medium text-fg-50 mb-1.5">
                  Small LLM Tokens
                </label>
                <div className="relative">
                  <MessageSquare size={12} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-fg-60" />
                  <input
                    type="number"
                    value={formData.monthlyLimits.smallLlmTokens === -1 ? "" : formData.monthlyLimits.smallLlmTokens}
                    onChange={(e) => {
                      const val = e.target.value === "" ? -1 : parseInt(e.target.value);
                      handleNestedInputChange('monthlyLimits', 'smallLlmTokens', val);
                    }}
                    className="w-full pl-8 pr-3 py-2 bg-bg-50 border border-bd-50 rounded-md text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-02 text-xs"
                    placeholder="Leave empty for unlimited"
                    disabled={isLoading}
                  />
                  {formData.monthlyLimits.smallLlmTokens === -1 && (
                    <span className="text-xs text-fg-60 mt-1 block">Unlimited</span>
                  )}
                </div>
              </div>

              {/* YouTube */}
              <div>
                <label className="block text-xs font-medium text-fg-50 mb-1.5">
                  YouTube Recaps *
                </label>
                <div className="relative">
                  <FileText size={12} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-fg-60" />
                  <input
                    type="number"
                    value={formData.monthlyLimits.youtube}
                    onChange={(e) => handleNestedInputChange('monthlyLimits', 'youtube', e.target.value)}
                    className="w-full pl-8 pr-3 py-2 bg-bg-50 border border-bd-50 rounded-md text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-02 text-xs"
                    placeholder="300"
                    required
                    disabled={isLoading}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Plan Preview - full width */}
          <div className="bg-bg-50 rounded-lg p-4 mb-6 w-full">
            <h4 className="text-sm font-medium text-fg-50 mb-2 font-mono">Plan Preview</h4>
            <div className="space-y-2 text-xs text-fg-60">
              <div className="flex justify-between">
                <span className="font-mono">Name:</span>
                <span className="text-fg-50 font-mono">{formData.name || '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-mono">Type:</span>
                <span className="text-fg-50 font-mono capitalize">{formData.planType || '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-mono">Base Cost:</span>
                <span className="text-fg-50 font-mono">${formData.baseCost || '0.00'}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-mono">Daily Tokens:</span>
                <span className="text-fg-50 font-mono">{formData.dailyLimits.highEndLlmTokens || '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-mono">Daily Images:</span>
                <span className="text-fg-50 font-mono">{formData.dailyLimits.images || '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-mono">Daily Searches:</span>
                <span className="text-fg-50 font-mono">{formData.dailyLimits.webSearches || '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-mono">Daily YouTube:</span>
                <span className="text-fg-50 font-mono">{formData.dailyLimits.youtube || '—'}</span>
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-6 justify-end">
            <button
              type="button"
              onClick={handleClose}
              className="px-3 py-1.5 text-xs bg-bg-50 text-fg-50 rounded-md hover:bg-bg-50 transition-colors"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-3 py-1.5 text-xs bg-ac-02 text-white dark:text-black rounded-md hover:bg-ac-01 transition-colors disabled:opacity-50"
              disabled={isLoading}
            >
              {isLoading ? "Creating..." : "Create Plan"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreatePlanModal; 